import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NpsButton, NpsContainer, NpsContent } from './index.styled';
import { useDispatch, useSelector } from "react-redux";
import { AppState } from '../../store/app-state';
import { fetchNpsData, submitNps } from '../../store/nps/nps-actions';
import { NpsAnswer, NpsInterface, NpsType } from '../../models/nps.models';
import NpsComponent from './components/nps-component';
import OpenComponent from './components/open-component';
import { useTranslation } from 'react-i18next';

export interface NpsComponentGenericProps {
    question: NpsInterface;
    setAnswer: (answer: NpsAnswer) => void;
}

const Nps: React.FC<{ show: boolean }> = ({ show }) => {
    const dispatch = useDispatch();
    const { nps } = useSelector((state: AppState) => state.npsState);
    const [answers, setAnswers] = useState<NpsAnswer[]>([]);
    const { t } = useTranslation('private-home-page');

    const handleSubmit = useCallback(() => {
        dispatch(submitNps(answers));
    }, [dispatch, answers]);

    const handleAnswer = useCallback((answer: NpsAnswer) => {
        const index = answers.findIndex((a) => a.question_id === answer.question_id);
        const copy = [...answers];
        if (index > -1) {
            copy[index] = answer;
        } else {
            copy.push(answer);
        }
        setAnswers(copy);
    }, [answers, setAnswers]);

    const getElement = useCallback((element: NpsInterface) => {
      switch (element.type) {
        case NpsType.NPS:
          return (
            <NpsComponent 
              key={element.id} 
              question={element} 
              setAnswer={handleAnswer}
            />
          );
        case NpsType.OPEN:
            return (
                <OpenComponent 
                    key={element.id} 
                    question={element} 
                    setAnswer={handleAnswer}
                />
            )
        default:
          return null;
      }
    }, [handleAnswer]);

    const disableSubmit: boolean = useMemo(() => {
        const requiredQuestions = nps.data.filter(n => n.required);
        const hasAnswer = requiredQuestions.map(
            rq => answers.some(a => a.question_id === rq.id)
        )
        return !hasAnswer.every(x => x)
    }, [nps, answers]);

    useEffect(() => {
        document.body.style.overflow = show ? 'hidden' : 'auto';
    }, [show]);

    useEffect(() => {
        show && dispatch(fetchNpsData());
    }, [dispatch, show]);

    return show ? (
        <NpsContainer>
            <NpsContent>
                {nps.data.map((e) => getElement(e))}
                <NpsButton disabled={disableSubmit} onClick={handleSubmit}>
                    {t('nps.submit')}
                </NpsButton>
            </NpsContent>
        </NpsContainer>
    ) : null;
};

export default Nps;
